import { useState } from 'react';

import { Combobox, HookFormResolver, Number } from '@dec/shared';
import { type HookFormMethods } from '@dec/shared';
import { Col, Row } from '@dec/shared';
import { useGetAllTaxesWitCurrentLang } from 'app/modules/taxes/hooks/useTaxes';
import { useTranslation } from 'react-i18next';

import { factoryYup } from '../../../core/factory-yup';
import { type CatalogModel } from '../../Parameters/Catalog/Catalog/models/types';
import { catalogs } from '../../Parameters/Catalog/Catalog/services/catalog';
import { useCatalogList } from '../../Parameters/Catalog/Catalog/services/useCatalog';
import { useGetPenaltiesTypes, useGetValuePenaltiesTypes } from '../hooks';
import { type Penalty, type PenaltyForm } from '../types';

const yup = factoryYup();
const validations = yup.object().shape({
  tax: yup.object().nullable().required(),
  accountType: yup.object().nullable().required(),
  transactionType: yup.object().nullable().required(),
  taxPeriodicityType: yup.object().nullable().required(),
  penaltyType: yup.object().nullable().required(),
  valueType: yup.object().nullable().required(),
  periodFrom: yup.number().nullable().required().min(2000),
  periodTo: yup.number().nullable().min(2000),
  value: yup.number().nullable().required()
});

// interface DataCatalogos{
//     taxes:Tax[]
//     penaltiesType:{id:string,name:string}[];
//     valuesPenaltiesType:{id:string,name:string}[];
//     accountTypes:CatalogModel[];
//     taxPeriodicityTypes:CatalogModel[];
//     transactionTypes:CatalogModel[];
//
// }

interface Props {
  nameForm: string;
  readOnly?: boolean;
  reject?: boolean;
  initialValues?: Penalty;
  onSubmit: (value: any) => void;
}
export const FormPenalty = ({ nameForm, onSubmit, readOnly, initialValues, reject }: Props) => {
  const { t } = useTranslation();
  const { data: taxes, loading: l1, error: e1 } = useGetAllTaxesWitCurrentLang();
  const [{ data: penaltiesType, loading: l2, error: e2 }] = useGetPenaltiesTypes();
  const [{ data: valuesPenaltiesType, loading: l3, error: e3 }] = useGetValuePenaltiesTypes();

  const [{ data: accountTypes, loading: l4, error: e4 }] = useCatalogList(catalogs.accountType);
  const [{ data: taxPeriodicityTypes, loading: l5, error: e5 }] = useCatalogList(
    catalogs.periodicityType
  );

  const [{ data: transactionTypes, loading: l6, error: e6 }] = useCatalogList(
    catalogs.transactionType
  );

  //const [transactioTypes, setTransactionTypes] = useState<CatalogModel[]>([]);

  const [parent, setParent] = useState(initialValues?.accountTypeId || '');

  const transactionTypesFiltersByParent = transactionTypes?.filter(
    (x) => x.parentId === parent && x.additionalFields.credit === false
  );

  // useEffect(() => {
  //     const execute = async () => {
  //         const result = await HttpApplication.get<CatalogModel[]>(
  //             `${baseCatalogUrl}/${catalogs.creditTypes}?parent=${parent}`
  //         );
  //         setTransactionTypes(result);
  //     };
  //     if (parent != undefined && parent !== '') {
  //         execute();
  //     }
  // }, [parent]);

  const isLoading = l1 || e1 || l2 || e2 || l3 || e3 || l4 || e4 || l5 || e5 || l6 || e6;

  if (isLoading) {
    return <></>;
  }

  const labelTax = t('taxpayer-payments.label.tax');
  const labelPenaltyType = t('parameters-catalog.label.type');
  const labeAccounType = t('form-version.labels.account-type');
  const labelTransactionType = t('form-version.labels.transaction-type');
  const labelPeriodFrom = t('penalties.label.periodFrom');
  const labelPeriodTo = t('penalties.label.periodTo');
  const labelValueType = t('common.label.valueType');
  const labelValue = t('taxpayer-current-account.label.value');
  const labelTaxPeriodicityTypeId = t('taxes.label.periodicityType');

  const newInitialValues: PenaltyForm | undefined = initialValues
    ? {
        value: initialValues.value,
        penaltyType: { id: initialValues.penaltyType },
        accountType: { id: initialValues.accountTypeId },
        periodFrom: initialValues.periodFrom,
        periodTo: initialValues.periodTo,
        valueType: { id: initialValues.valueType },
        tax: { id: initialValues.taxId },
        taxPeriodicityType: { id: initialValues.taxPeriodicityTypeId },
        transactionType: { id: initialValues.transactionTypeId }
      }
    : undefined;
  return (
    <HookFormResolver
      initialValues={newInitialValues}
      validations={validations}
      nameForm={nameForm}
      onSubmit={onSubmit}
    >
      {({ watch, register }: HookFormMethods) => {
        return (
          <>
            <Row>
              <Col xl={6} md={6} lg={6}>
                <Combobox label={labelTax} name="tax" display="name" code="id" data={taxes} />
              </Col>
              <Col>
                <Combobox
                  data={accountTypes}
                  code={'id'}
                  display={'name'}
                  label={labeAccounType}
                  name="accountType"
                  child="transactionType"
                  whenChange={(catalog: CatalogModel) => {
                    if (catalog && catalog.id != undefined) {
                      setParent(catalog.id);
                    } else {
                      setParent('');
                    }
                  }}
                />
              </Col>

              <Col>
                <Combobox
                  label={labelTransactionType}
                  name="transactionType"
                  data={transactionTypesFiltersByParent || []}
                  code="id"
                  display="name"
                />
              </Col>
            </Row>

            <Row>
              <Col xl={6} md={6} lg={6}>
                <Combobox
                  label={labelTaxPeriodicityTypeId}
                  name="taxPeriodicityType"
                  display="name"
                  code="id"
                  data={taxPeriodicityTypes}
                />
              </Col>
              <Col>
                <Number placeholder="yyyymm" label={labelPeriodFrom} name="periodFrom" />
              </Col>
              <Col>
                <Number placeholder="yyyymm" label={labelPeriodTo} name="periodTo" />
              </Col>
            </Row>
            <Row>
              <Col xl={6} md={6} lg={6}>
                <Combobox
                  label={labelPenaltyType}
                  name="penaltyType"
                  display="name"
                  code="id"
                  data={penaltiesType}
                />
              </Col>
              <Col>
                <Combobox
                  label={labelValueType}
                  name="valueType"
                  display="name"
                  code="id"
                  data={valuesPenaltiesType}
                />
              </Col>
              <Col>
                <Number label={labelValue} name="value" allowDecimal={true} />
              </Col>
            </Row>
          </>
        );
      }}
    </HookFormResolver>
  );
};
